import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from '../../services/api';
import { normalize } from '../../utils/normalize';
import { useAuth } from '../../context/AuthContext';
import { canWrite } from '../../utils/roles';
import PersonnelSearch from '../../components/PersonnelSearch';
import { groupLogicalInventory, logicalInventoryKey } from '../../utils/logicalInventory';

function groupByDay(events) {
  const map = new Map();
  for (const ev of events) {
    const key = ev.event_date;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(ev);
  }
  return [...map.entries()];
}

function dayLabel(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // dateStr puede venir como "YYYY-MM-DD" o timestamp ISO completo
  const d = new Date(String(dateStr).slice(0, 10) + 'T12:00:00');
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Hoy';
  if (diff === 1) return 'Mañana';
  const weekday = d.toLocaleDateString('es-AR', { weekday: 'short' }).replace('.', '');
  return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)} ${d.getDate()}/${d.getMonth() + 1}`;
}

const VEHICLES = [
  'Camion Grande',
  'Camion Mediano Azul',
  'Camion Mediano Rojo',
  'Camion Chico',
  'Fiorino Andres',
  'Fiorino Alallon',
  'Vehículo particular',
];

const STATUS_LABELS = {
  draft: { label: 'Borrador', bg: 'var(--surface)', color: 'var(--text-dim)' },
  in_transit: { label: 'En tránsito', bg: 'var(--yellow-dim, #3d2e00)', color: 'var(--yellow, #f59e0b)' },
  completed: { label: 'Completado', bg: 'var(--green-dim)', color: 'var(--green)' },
};

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })
    + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false, hourCycle: 'h23' });
}

// ── CreateTransferModal ──────────────────────────────────────────────────────

function CreateTransferModal({ onClose, onCreated }) {
  const [vehicle, setVehicle] = useState('');
  const [origenName, setOrigenName] = useState('');
  const [origenId, setOrigenId] = useState(null);
  const [origenSuggestions, setOrigenSuggestions] = useState([]);
  const [showOrigenSugg, setShowOrigenSugg] = useState(false);
  const [destinoName, setDestinoName] = useState('');
  const [destinoId, setDestinoId] = useState(null);
  const [destinoSuggestions, setDestinoSuggestions] = useState([]);
  const [showDestinoSugg, setShowDestinoSugg] = useState(false);
  const [eventName, setEventName] = useState('');
  const [eventId, setEventId] = useState(null);
  const [eventSugg, setEventSugg] = useState([]);
  const [showEventSugg, setShowEventSugg] = useState(false);
  const eventInputRef = useRef(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [destCargaItems, setDestCargaItems] = useState([]);
  const [loadingCarga, setLoadingCarga] = useState(false);
  const [itemAssociations, setItemAssociations] = useState({});
  const [newItemData, setNewItemData] = useState({});
  const [locationItems, setLocationItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Load upcoming events on mount
  useEffect(() => {
    api.get('/events/upcoming?days=7')
      .then(({ data }) => setUpcomingEvents(data))
      .catch(console.error);
  }, []);

  // Debounced origen suggestions
  useEffect(() => {
    if (origenName.length < 2) { setOrigenSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/locations?search=${encodeURIComponent(origenName)}`);
        setOrigenSuggestions(data);
      } catch (e) { console.error(e); }
    }, 250);
    return () => clearTimeout(t);
  }, [origenName]);

  // Debounced destino suggestions
  useEffect(() => {
    if (destinoName.length < 2) { setDestinoSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/locations?search=${encodeURIComponent(destinoName)}`);
        setDestinoSuggestions(data.filter(l => l.id !== origenId));
      } catch (e) { console.error(e); }
    }, 250);
    return () => clearTimeout(t);
  }, [destinoName, origenId]);

  // Load items when origen is selected
  useEffect(() => {
    if (!origenId) { setLocationItems([]); setSelectedItems({}); return; }
    setLoadingItems(true);
    api.get(`/locations/${origenId}`)
      .then(({ data }) => {
        setLocationItems(data.items || []);
        setSelectedItems({});
      })
      .catch(console.error)
      .finally(() => setLoadingItems(false));
  }, [origenId]);

  // Event search
  useEffect(() => {
    if (eventName.length < 2) { setEventSugg([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/events/search?q=${encodeURIComponent(eventName)}`);
        setEventSugg(data);
      } catch (e) { console.error(e); }
    }, 250);
    return () => clearTimeout(t);
  }, [eventName]);

  // Load pending CARGA items when event or destination location changes
  useEffect(() => {
    if (!eventId) { setDestCargaItems([]); return; }
    setLoadingCarga(true);
    const params = new URLSearchParams();
    if (destinoId) params.set('location_id', String(destinoId));
    api.get(`/checklists/event/${eventId}/pending-items?${params}`)
      .then(({ data }) => setDestCargaItems(data))
      .catch(console.error)
      .finally(() => setLoadingCarga(false));
  }, [eventId, destinoId]);

  const filteredItems = locationItems.filter(item => {
    if (!itemSearch.trim()) return true;
    const words = normalize(itemSearch).split(/\s+/).filter(Boolean);
    const hay = normalize(`${item.name} ${item.category || ''} ${item.subcategory || ''}`);
    return words.every(w => hay.includes(w));
  });

  const selectedCount = Object.values(selectedItems).filter(Boolean).length;
  const allFiltered = filteredItems.length > 0 && filteredItems.every(i => selectedItems[i.id]);
  const toggleLogicalGroup = (group) => {
    const everySelected = group.items.every(item => selectedItems[item.id]);
    setSelectedItems(prev => {
      const next = { ...prev };
      for (const item of group.items) next[item.id] = !everySelected;
      return next;
    });
  };
  const handleSelectAll = () => {
    setSelectedItems(prev => {
      const next = { ...prev };
      for (const i of filteredItems) next[i.id] = !allFiltered;
      return next;
    });
  };

  const handleCreate = async () => {
    setError('');
    if (!origenId) { setError('Seleccioná el origen'); return; }
    if (!destinoName.trim()) { setError('Ingresá el destino'); return; }
    if (selectedCount === 0) { setError('Seleccioná al menos un ítem'); return; }
    setSaving(true);
    try {
      let toLocationId = destinoId;
      if (!toLocationId) {
        const allRes = await api.get('/locations');
        const existing = allRes.data.find(l => normalize(l.name) === normalize(destinoName.trim()));
        if (existing) {
          toLocationId = existing.id;
        } else {
          const created = await api.post('/locations', { name: destinoName.trim(), force: true });
          toLocationId = created.data.id;
        }
      }

      const transferRes = await api.post('/transfers', {
        from_location_id: origenId,
        to_location_id: toLocationId,
        vehicle: vehicle || null,
      });
      const transferId = transferRes.data.id;

      const toAdd = locationItems.filter(i => selectedItems[i.id]);
      for (const item of toAdd) {
        if (item.unconsolidated) {
          await api.post(`/transfers/${transferId}/items`, { checklist_item_name: item.name });
        } else {
          await api.post(`/transfers/${transferId}/items`, { inventory_item_id: item.id });
        }
      }

      await api.patch(`/transfers/${transferId}/dispatch`);

      if (eventId) {
        const selectedItemsList = locationItems.filter(i => selectedItems[i.id]);
        const assocList = selectedItemsList
          .map(item => {
            const groupKey = logicalInventoryKey(item);
            const target = itemAssociations[item.id];
            const isUnconsolidated = !!item?.unconsolidated;
            // No selection or empty → auto-create as new item in dest checklist
            const effectiveTarget = (target === null || target === undefined || target === '') ? 'new' : target;
            const autoName = item.name || item.item_name || item.checklist_item_name || 'Equipo';
            return {
              inventory_item_id: isUnconsolidated ? null : item.id,
              checklist_item_name: isUnconsolidated ? (item.name || null) : null,
              target_checklist_item_id: effectiveTarget === 'new' ? null : parseInt(effectiveTarget),
              is_new: effectiveTarget === 'new',
              new_item: effectiveTarget === 'new' ? {
                name: newItemData[groupKey]?.name || autoName,
                zone: newItemData[groupKey]?.zone || '',
                category: newItemData[groupKey]?.category || item?.category || '',
              } : null,
            };
          })
          .filter(a => a.inventory_item_id || a.checklist_item_name);
        if (assocList.length > 0) {
          await api.post(`/transfers/${transferId}/associate-event`, { event_id: eventId, item_associations: assocList });
        }
      }

      onCreated();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al crear el transfer');
      setSaving(false);
    }
  };

  // Agrupa la presentación por categoría y modelo, sin perder las unidades
  // concretas seleccionadas que la API necesita para la trazabilidad.
  const grouped = {};
  for (const item of groupLogicalInventory(filteredItems)) {
    const key = item.category || 'Sin categoría';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-title" style={{ marginBottom: 16 }}>Nueva logística</div>

        {error && <div className="inv-error" style={{ marginBottom: 10 }}>{error}</div>}

        <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Vehículo */}
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>Vehículo</label>
            <select className="input" value={vehicle} onChange={e => setVehicle(e.target.value)}>
              <option value="">SIN ESPECIFICAR</option>
              {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          {/* Origen */}
          <div style={{ position: 'relative' }}>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>Origen</label>
            <input
              className="input"
              placeholder="Buscar locación de origen..."
              value={origenName}
              onChange={e => { setOrigenName(e.target.value); setOrigenId(null); setShowOrigenSugg(true); }}
              onFocus={() => setShowOrigenSugg(true)}
              onBlur={() => setTimeout(() => setShowOrigenSugg(false), 150)}
              onKeyDown={e => {
                if (e.key === 'Enter' && showOrigenSugg && origenSuggestions.length > 0) {
                  e.preventDefault();
                  const first = origenSuggestions[0];
                  setOrigenName(first.name); setOrigenId(first.id); setShowOrigenSugg(false);
                }
              }}
            />
            {showOrigenSugg && origenSuggestions.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)', marginTop: 2,
              }}>
                {origenSuggestions.map(loc => (
                  <div
                    key={loc.id}
                    onMouseDown={() => { setOrigenName(loc.name); setOrigenId(loc.id); setShowOrigenSugg(false); }}
                    style={{ padding: '10px 12px', minHeight: 44, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    {loc.name}
                    {loc.is_home && <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>depósito</span>}
                    <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>· {loc.item_count} ítems</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Destino */}
          <div style={{ position: 'relative' }}>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>Destino</label>
            <input
              className="input"
              placeholder="Escribí el destino..."
              value={destinoName}
              onChange={e => { setDestinoName(e.target.value); setDestinoId(null); setShowDestinoSugg(true); }}
              onFocus={() => setShowDestinoSugg(true)}
              onBlur={() => setTimeout(() => setShowDestinoSugg(false), 150)}
              onKeyDown={e => {
                if (e.key === 'Enter' && showDestinoSugg && destinoSuggestions.length > 0) {
                  e.preventDefault();
                  const first = destinoSuggestions[0];
                  setDestinoName(first.name); setDestinoId(first.id); setShowDestinoSugg(false);
                }
              }}
            />
            {showDestinoSugg && destinoSuggestions.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)', marginTop: 2,
              }}>
                {destinoSuggestions.map(loc => (
                  <div
                    key={loc.id}
                    onMouseDown={() => { setDestinoName(loc.name); setDestinoId(loc.id); setShowDestinoSugg(false); }}
                    style={{ padding: '10px 12px', minHeight: 44, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    {loc.name}
                    {loc.is_home && <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>depósito</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Evento destino (opcional) */}
          <div style={{ position: 'relative' }}>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>
              Evento destino <span style={{ fontWeight: 400, fontSize: 11 }}>(opcional)</span>
            </label>
            <input
              ref={eventInputRef}
              className="input"
              placeholder="Buscar evento..."
              value={eventName}
              onChange={e => { setEventName(e.target.value); setEventId(null); setShowEventSugg(true); setDestCargaItems([]); setItemAssociations({}); }}
              onFocus={() => setShowEventSugg(true)}
              onBlur={() => setTimeout(() => setShowEventSugg(false), 150)}
              onKeyDown={e => {
                if (e.key === 'Enter' && showEventSugg) {
                  const sugg = eventName.length < 2 ? upcomingEvents : eventSugg;
                  if (sugg.length > 0) {
                    e.preventDefault();
                    const first = sugg[0];
                    setEventName(first.name); setEventId(first.id); setShowEventSugg(false);
                    if (first.place) { setDestinoName(first.place); setDestinoId(null); }
                  }
                }
              }}
            />
            {showEventSugg && eventInputRef.current && (() => {
              const showUpcoming = eventName.length < 2;
              const hasContent = showUpcoming ? upcomingEvents.length > 0 : eventSugg.length > 0;
              if (!hasContent) return null;
              const r = eventInputRef.current.getBoundingClientRect();
              const menuStyle = {
                position: 'fixed', top: r.bottom + 2, left: r.left, width: r.width,
                zIndex: 9999, maxHeight: 280, overflowY: 'auto',
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              };
              const pick = ev => { setEventName(ev.name); setEventId(ev.id); setShowEventSugg(false); if (ev.place) { setDestinoName(ev.place); setDestinoId(null); } };
              return ReactDOM.createPortal(
                <div style={menuStyle}>
                  {showUpcoming ? (
                    groupByDay(upcomingEvents).map(([date, evs]) => (
                      <div key={date}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dim)', padding: '6px 12px 3px', textTransform: 'uppercase', letterSpacing: '0.08em', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                          {dayLabel(date)}
                        </div>
                        {evs.map(ev => (
                          <div
                            key={ev.id}
                            onMouseDown={() => pick(ev)}
                            style={{ padding: '7px 12px', cursor: 'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}
                          >
                            <div style={{ fontSize: 13 }}>{ev.name}</div>
                            {(ev.place || ev.start_time) && (
                              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 1 }}>
                                {ev.place && <span>{ev.place}</span>}
                                {ev.place && ev.start_time && <span> · </span>}
                                {ev.start_time && <span>{ev.start_time}</span>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))
                  ) : (
                    eventSugg.map(ev => (
                      <div
                        key={ev.id}
                        onMouseDown={() => pick(ev)}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 8 }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <span>{ev.name}</span>
                        {ev.event_date && <span style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{dayLabel(ev.event_date)}</span>}
                      </div>
                    ))
                  )}
                </div>,
                document.body
              );
            })()}
          </div>

          {/* Items */}
          {origenId && (
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6 }}>
                Ítems a transportar
                {selectedCount > 0 && <span style={{ color: 'var(--accent)', marginLeft: 6 }}>({selectedCount} seleccionados)</span>}
              </label>
              {loadingItems ? (
                <div style={{ fontSize: 13, color: 'var(--text-dim)', padding: '8px 0' }}>Cargando ítems...</div>
              ) : locationItems.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-dim)', padding: '8px 0' }}>No hay ítems en esta locación.</div>
              ) : (
                <>
                  <input
                    className="input"
                    placeholder="Buscar equipo..."
                    value={itemSearch}
                    onChange={e => setItemSearch(e.target.value)}
                    style={{ marginBottom: 6 }}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ fontSize: 12, padding: '3px 8px', marginBottom: 6 }}
                    onClick={handleSelectAll}
                  >
                    {allFiltered ? 'Deseleccionar todos' : 'Seleccionar todos'}
                  </button>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Object.entries(grouped).map(([cat, catItems]) => (
                      <div key={cat}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', padding: '4px 0 2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{cat}</div>
                        {catItems.map(group => {
                          const checked = group.items.every(item => selectedItems[item.id]);
                          const partiallySelected = !checked && group.items.some(item => selectedItems[item.id]);
                          return (
                            <div
                              key={group.key}
                              onClick={() => toggleLogicalGroup(group)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '5px 8px', borderRadius: 5, cursor: 'pointer',
                                background: checked || partiallySelected ? 'var(--surface2)' : undefined,
                              }}
                            >
                              <div style={{
                                width: 15, height: 15, borderRadius: 3, flexShrink: 0,
                                border: '1.5px solid var(--border)',
                                background: checked ? 'var(--accent, #6366f1)' : partiallySelected ? 'var(--accent-dim, #bfdbfe)' : undefined,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {checked && (
                                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                                    <polyline points="20,6 9,17 4,12"/>
                                  </svg>
                                )}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <strong style={{ fontSize: 13 }}>{group.items.length} ×</strong>
                                <span style={{ fontSize: 13, marginLeft: 4 }}>{group.name}</span>
                              </div>
                              {group.unconsolidated && (
                                <span style={{ fontSize: 10, color: 'var(--yellow, #f59e0b)', flexShrink: 0 }}>Sin consolidar</span>
                              )}
                              {!group.unconsolidated && group.items.some(item => !item.is_operational) && (
                                <span style={{ fontSize: 10, color: 'var(--red, #ef4444)', flexShrink: 0 }}>No operativo</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {/* Asociar a checklist del evento destino */}
          {eventId && selectedCount > 0 && (
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6 }}>
                Asociar a checklist CARGA de "{eventName}"
              </label>
              {loadingCarga ? (
                <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Cargando checklist...</div>
              ) : destCargaItems.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>No hay checklist CARGA para ese evento.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {groupLogicalInventory(locationItems.filter(i => selectedItems[i.id])).map(group => {
                    const associationKey = group.key;
                    const applyAssociation = value => {
                      setItemAssociations(prev => {
                        const next = { ...prev };
                        for (const item of group.items) next[item.id] = value;
                        return next;
                      });
                    };
                    const association = itemAssociations[group.items[0]?.id] ?? '';
                    return (
                    <div key={group.key}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: association === 'new' ? 4 : 0 }}>
                        <span style={{ fontSize: 12, flex: 1, minWidth: 0, color: 'var(--text-dim)' }}>
                          {group.items.length} × {group.name}
                          {group.unconsolidated && <span style={{ marginLeft: 4, color: 'var(--yellow, #f59e0b)' }}>(sin consolidar)</span>}
                        </span>
                        <select
                          className="input"
                          style={{ flex: 1, fontSize: 12, height: 32, padding: '0 8px' }}
                          value={association}
                          onChange={e => {
                            const v = e.target.value;
                            applyAssociation(v === '' ? null : v === 'new' ? 'new' : parseInt(v));
                          }}
                        >
                          <option value="">Sin asociar</option>
                          {destCargaItems.map(ci => (
                            <option key={ci.id} value={ci.id}>{ci.name}</option>
                          ))}
                          <option value="new">+ Nuevo ítem</option>
                        </select>
                      </div>
                      {association === 'new' && (
                        <div style={{ padding: '6px 8px', background: 'var(--surface)', borderRadius: 5, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <input className="input" placeholder="Nombre del ítem" style={{ fontSize: 12 }}
                            value={newItemData[associationKey]?.name ?? group.name}
                            onChange={e => setNewItemData(prev => ({ ...prev, [associationKey]: { ...(prev[associationKey] || {}), name: e.target.value } }))} />
                          <div style={{ display: 'flex', gap: 4 }}>
                            <input className="input" placeholder="Zona (ej: PATIO)" style={{ fontSize: 12, flex: 1 }}
                              value={newItemData[associationKey]?.zone ?? ''}
                              onChange={e => setNewItemData(prev => ({ ...prev, [associationKey]: { ...(prev[associationKey] || {}), zone: e.target.value } }))} />
                            <input className="input" placeholder="Categoría" style={{ fontSize: 12, flex: 1 }}
                              value={newItemData[associationKey]?.category ?? group.category ?? ''}
                              onChange={e => setNewItemData(prev => ({ ...prev, [associationKey]: { ...(prev[associationKey] || {}), category: e.target.value } }))} />
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 12, marginTop: 10, borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button
            className="btn btn-primary"
            onClick={handleCreate}
            disabled={saving || selectedCount === 0}
          >
            {saving ? 'Creando...' : `Crear y despachar (${selectedCount})`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── TransferRow (accordion) ──────────────────────────────────────────────────

function dedupeItems(items) {
  if (!items) return [];
  const packIds = new Set(items.filter(i => i.pack_id).map(i => i.pack_id));
  return items.filter(item => {
    if (item.pack_id) return true;
    if (item.item_pack_id && packIds.has(item.item_pack_id)) return false;
    return true;
  });
}

const PackIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

function EditTransferModal({ transfer, onClose, onSaved }) {
  const [vehicle, setVehicle] = useState(transfer.vehicle || VEHICLES[0]);
  const [responsibleId, setResponsibleId] = useState(String(transfer.responsible_id || ''));
  const [personnel, setPersonnel] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/personnel').then(r => setPersonnel(r.data)).catch(console.error);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/transfers/${transfer.id}`, {
        vehicle,
        responsible_id: responsibleId ? parseInt(responsibleId) : null,
      });
      onSaved(data);
    } catch (e) {
      alert(e.response?.data?.error || 'Error al guardar');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title" style={{ marginBottom: 16 }}>Editar logística</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>Vehículo</label>
            <select className="input" value={vehicle} onChange={e => setVehicle(e.target.value)}>
              {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <PersonnelSearch personnel={personnel} value={responsibleId} onChange={setResponsibleId} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TransferRow({ t, onDelete, onUpdated, isAdmin }) {
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const s = STATUS_LABELS[t.status] || {};

  const itemNodes = items === null ? null : groupLogicalInventory(dedupeItems(items)).map(group => (
    <div key={group.key} style={{ fontSize: 12, color: 'var(--text-dim)', padding: '1px 0', display: 'flex', alignItems: 'center', gap: 5 }}>
      {group.pack_id && <PackIcon />}
      <span><strong>{group.items.length} ×</strong> {group.name}</span>
      {group.unconsolidated && (
        <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: 'var(--yellow-dim, #3d2e00)', color: 'var(--yellow, #f59e0b)', fontWeight: 600 }}>Sin consolidar</span>
      )}
    </div>
  ));

  const handleToggle = async () => {
    if (!expanded && items === null) {
      setLoadingItems(true);
      try {
        const { data } = await api.get(`/transfers/${t.id}`);
        setItems(data.items || []);
      } catch (e) {
        console.error(e);
        setItems([]);
      } finally {
        setLoadingItems(false);
      }
    }
    setExpanded(prev => !prev);
  };

  return (
    <div style={{
      borderRadius: 8, border: '1px solid var(--border)',
      background: 'var(--surface2)', overflow: 'hidden',
    }}>
      <div
        onClick={handleToggle}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer' }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-dim)" strokeWidth="1.8" style={{ flexShrink: 0 }}>
          <rect x="1" y="3" width="15" height="13" rx="1"/>
          <path d="M16 8h4l3 5v3h-7V8z"/>
          <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
        </svg>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{t.vehicle || '—'}</span>
            <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{t.from_location_name} → {t.to_location_name}</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(t.responsible_name || t.created_by_name) && <span>{t.responsible_name || t.created_by_name}</span>}
            <span>· {t.item_count} ítem{t.item_count !== 1 ? 's' : ''}</span>
            <span>· {formatDate(t.created_at)}</span>
            {t.event_name && <span>· {t.event_name}</span>}
          </div>
        </div>
        <span style={{
          fontSize: 11, padding: '2px 8px', borderRadius: 4, flexShrink: 0,
          background: s.bg, color: s.color, fontWeight: 600,
        }}>
          {s.label}
        </span>
        {isAdmin && (
          <button
            className="btn btn-icon btn-ghost"
            style={{ color: 'var(--text-dim)', width: 26, height: 26, flexShrink: 0 }}
            title="Editar"
            onClick={e => { e.stopPropagation(); setShowEdit(true); }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
        )}
        {(t.status !== 'completed' || isAdmin) && (
          <button
            className="btn btn-icon btn-ghost"
            style={{ color: 'var(--text-dim)', width: 26, height: 26, flexShrink: 0 }}
            title="Eliminar"
            onClick={e => { e.stopPropagation(); onDelete(t); }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6M14 11v6"/>
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
            </svg>
          </button>
        )}
        <svg
          width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-dim)" strokeWidth="2"
          style={{ flexShrink: 0, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {loadingItems ? (
            <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '4px 0' }}>Cargando ítems...</div>
          ) : (items || []).length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '4px 0' }}>Sin ítems registrados.</div>
          ) : itemNodes}
        </div>
      )}

      {showEdit && (
        <EditTransferModal
          transfer={t}
          onClose={() => setShowEdit(false)}
          onSaved={(updated) => { setShowEdit(false); onUpdated(updated); }}
        />
      )}
    </div>
  );
}

// ── LogisticsPage ────────────────────────────────────────────────────────────

export default function LogisticsPage() {
  const { user } = useAuth();
  const isAdmin = canWrite(user, 'logistica');
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterVehicle, setFilterVehicle] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterVehicle) params.set('vehicle', filterVehicle);
      const { data } = await api.get(`/transfers?${params.toString()}`);
      setTransfers(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterVehicle]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = (transfer) => setDeleteTarget(transfer);

  const confirmDelete = async () => {
    try {
      await api.delete(`/transfers/${deleteTarget.id}`);
      setTransfers(prev => prev.filter(t => t.id !== deleteTarget.id));
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="module-sections">
      <div className="page-header module-sections-header">
        <div>
          <h1 className="page-title module-sections-title">Logística</h1>
          <p className="page-subtitle module-sections-subtitle">Transfers entre locaciones</p>
        </div>
        <button className="btn btn-primary" style={{ gap: 6 }} onClick={() => setShowCreateModal(true)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Nueva logística
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <select
          className="input"
          style={{ width: 'auto', minWidth: 140 }}
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="">Todos los estados</option>
          <option value="draft">Borrador</option>
          <option value="in_transit">En tránsito</option>
          <option value="completed">Completados</option>
        </select>
        <select
          className="input"
          style={{ width: 'auto', minWidth: 160 }}
          value={filterVehicle}
          onChange={e => setFilterVehicle(e.target.value)}
        >
          <option value="">Todos los vehículos</option>
          {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }} />
          Cargando...
        </div>
      ) : transfers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-dim)', fontSize: 14 }}>
          No hay logísticas{filterStatus || filterVehicle ? ' con los filtros aplicados' : ' todavía'}.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {transfers.map(t => (
            <TransferRow
              key={t.id}
              t={t}
              onDelete={handleDelete}
              onUpdated={(updated) => setTransfers(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x))}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateTransferModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); load(); }}
        />
      )}

      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Eliminar logística</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 8 }}>
              Vas a eliminar la logística:
            </p>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>
              {deleteTarget.vehicle || 'Sin vehículo'}
            </p>
            <p style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 24 }}>
              {deleteTarget.from_location_name} → {deleteTarget.to_location_name}
              {deleteTarget.status !== 'completed' ? ' · Los ítems en tránsito vuelven al origen.' : ''}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>Cancelar</button>
              <button className="btn btn-danger" onClick={confirmDelete}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
